import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDepartments } from "@/hooks";
import type { Policy } from "@/types/policy";

interface ChecklistItem {
  id: string;
  title: string;
  description: string | null;
  order_index: number;
}

interface ImportChecklistTasksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportChecklistTasksModal({ open, onOpenChange }: ImportChecklistTasksModalProps) {
  const { profile } = useAuth();
  const { departments } = useDepartments();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<string>("");
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [priority, setPriority] = useState<'p0' | 'p1' | 'p2' | 'p3'>('p2');
  const [assignee, setAssignee] = useState<string>("");
  const [department, setDepartment] = useState<string>("");

  // Fetch approved policies
  useEffect(() => {
    if (open && profile?.company_id) {
      fetchApprovedPolicies();
    }
  }, [open, profile?.company_id]);

  const fetchApprovedPolicies = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('policies')
        .select('id, title')
        .eq('company_id', profile!.company_id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPolicies(data || []);
    } catch (err) {
      console.error('Error fetching policies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load checklist items when policy is selected
  useEffect(() => {
    if (selectedPolicy) {
      loadChecklistItems();
    } else {
      setChecklistItems([]);
      setSelectedItems(new Set());
    }
  }, [selectedPolicy]);

  const loadChecklistItems = async () => {
    if (!profile?.company_id) return;

    try {
      const { data, error } = await supabase
        .from('compliance_checklists')
        .select(`
          items:compliance_checklist_items (
            id,
            title,
            description,
            order_index
          )
        `)
        .eq('policy_id', selectedPolicy)
        .eq('company_id', profile.company_id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data?.items) {
        const sortedItems = data.items.sort((a: ChecklistItem, b: ChecklistItem) => a.order_index - b.order_index);
        setChecklistItems(sortedItems);
        setSelectedItems(new Set(sortedItems.map((item: ChecklistItem) => item.id)));
      }
    } catch (err) {
      console.error('Error loading checklist:', err);
    }
  };

  const handleImport = async () => {
    if (!selectedPolicy || selectedItems.size === 0 || !profile?.company_id || !profile?.id) return;

    setImporting(true);
    try {
      const itemsToImport = checklistItems.filter(item => selectedItems.has(item.id));

      for (const item of itemsToImport) {
        await supabase
          .from('compliance_tasks')
          .insert({
            company_id: profile.company_id,
            title: item.title,
            description: item.description,
            priority,
            status: 'not_started',
            policy_id: selectedPolicy,
            checklist_item_id: item.id,
            department_id: department || null,
            primary_assignee_id: assignee || null,
            created_by: profile.id,
          });
      }

      onOpenChange(false);
      reset();
    } catch (err) {
      console.error('Error importing tasks:', err);
      alert('Failed to import tasks. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setSelectedPolicy("");
    setChecklistItems([]);
    setSelectedItems(new Set());
    setPriority('p2');
    setAssignee("");
    setDepartment("");
  };

  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedItems(new Set(checklistItems.map(item => item.id)));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Import Tasks from Policy Checklist</DialogTitle>
          <DialogDescription>
            Select an approved policy to import its compliance checklist items as tasks.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Policy Selection */}
            <div className="space-y-2">
              <Label>Select Policy</Label>
              <Select value={selectedPolicy} onValueChange={setSelectedPolicy}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a policy..." />
                </SelectTrigger>
                <SelectContent>
                  {policies.map((policy) => (
                    <SelectItem key={policy.id} value={policy.id}>
                      {policy.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPolicy && (
              <>
                {/* Bulk Settings */}
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/50">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={priority} onValueChange={(val) => setPriority(val as any)}>
                      <SelectTrigger id="priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="p0">P0 - Critical</SelectItem>
                        <SelectItem value="p1">P1 - High</SelectItem>
                        <SelectItem value="p2">P2 - Medium</SelectItem>
                        <SelectItem value="p3">P3 - Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Select value={department} onValueChange={setDepartment}>
                      <SelectTrigger id="department">
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments?.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Checklist Items */}
                {checklistItems.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">
                        Checklist Items ({selectedItems.size} selected)
                      </Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={selectAll}>
                          Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={deselectAll}>
                          Deselect All
                        </Button>
                      </div>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto space-y-2">
                      {checklistItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50"
                        >
                          <Checkbox
                            checked={selectedItems.has(item.id)}
                            onCheckedChange={() => toggleItem(item.id)}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{item.title}</div>
                            {item.description && (
                              <div className="text-sm text-muted-foreground mt-1">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No checklist items found for this policy</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!selectedPolicy || selectedItems.size === 0 || importing}>
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Import {selectedItems.size} Task{selectedItems.size !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

